import { CURRENT_COUNTRY } from '../../../../../../_config/constants/countries.constant';
import { constant } from '../constant/config.constant';
import {
	hasTypology,
	pickMatchingResult,
} from '../../../../../../pages/checkout/pages/withdrawal/utils/addressPayload';

const normalizeAddress = (address, { noNumber = false } = {}) => {
	if (!address || typeof address !== 'string') return '';

	const parts = address
		.split(',')
		.map((part) => part.trim())
		.filter(Boolean);

	if (parts.length < 3) {
		return address;
	}

	const region = parts[0];
	const commune = parts[1];
	let street = parts.slice(2).join(', ');
	if (noNumber && street && !/\bS\/N\b/i.test(street)) {
		street = `${street} S/N`;
	}

	return `${street}, ${commune}, ${region}, ${CURRENT_COUNTRY}`;
};

// Retry when the street name collides with a locality (e.g. Santiago 1320 → city-only partial_match).
export const buildCalleRetryAddress = (address, { noNumber = false } = {}) => {
	const normalized = normalizeAddress(address, { noNumber });
	if (!normalized) return null;

	const parts = normalized
		.split(',')
		.map((part) => part.trim())
		.filter(Boolean);

	if (parts.length < 4) return null;

	const country = parts[parts.length - 1];
	const region = parts[parts.length - 2];
	const commune = parts[parts.length - 3];
	const street = parts.slice(0, -3).join(', ');

	if (!street || hasTypology(street)) return null;

	return `Calle ${street}, ${commune}, ${region}, ${country}`;
};

const pickExactResult = (results) => results?.find((result) => result?.partial_match !== true) || null;

const pickBestResult = (results, street, commune, noNumber) => {
	if (street) {
		const matched = pickMatchingResult(results, street, commune, noNumber);
		if (matched) return matched;
	}
	return pickExactResult(results);
};

const runGeocode = (geocoder, query) =>
	new Promise((resolve, reject) => {
		geocoder.geocode({ address: query }, (results, status) => {
			if (status !== 'OK' || !results?.length) {
				reject(new Error('No se pudo geocodificar la dirección'));
				return;
			}
			resolve(results);
		});
	});

const resolveFromResult = (exactResult) => {
	const location = exactResult.geometry.location;

	return {
		lat: Number(location.lat().toFixed(6)),
		lng: Number(location.lng().toFixed(6)),
		formattedAddress: exactResult.formatted_address,
		addressComponents: exactResult.address_components || [],
		partialMatch: !!exactResult.partial_match,
		rawResult: exactResult,
	};
};

export const geocodeAddress = (address = constant.defaultAddress, options = {}) =>
	new Promise(async (resolve, reject) => {
		try {
			if (!window.google?.maps?.Geocoder) {
				reject(new Error('Google Maps no está disponible'));
				return;
			}

			const { noNumber = false, street = '', commune = '' } = options;
			const normalizedAddress = normalizeAddress(address, { noNumber });
			const geocoder = new window.google.maps.Geocoder();

			let results = await runGeocode(geocoder, normalizedAddress);
			let exactResult = pickBestResult(results, street, commune, noNumber);

			if (!exactResult) {
				const retryAddress = buildCalleRetryAddress(address, { noNumber });
				if (retryAddress) {
					results = await runGeocode(geocoder, retryAddress);
					exactResult = pickBestResult(results, street, commune, noNumber);
				}
			}

			if (!exactResult) {
				reject(new Error('Dirección inexistente'));
				return;
			}

			resolve(resolveFromResult(exactResult));
		} catch (error) {
			reject(error);
		}
	});
